/** @file liveSync: the puppet's live WebSocket for the store: missed and new commits, socket state, close. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import type { Pose3DError } from '@zilo/pose3d-std/errors/pose3DError';
import type { PuppetState } from '../state/editorTypes';
import { asPose3DError } from './asPose3DError';
import type { EditorApi } from './editorApi';

/** A commit the socket reported (a missed one from `welcome`, or a live `committed`). */
export type LiveCommit = { revision: number; actor: string; commands: Command[] };

/** What the store hears from the socket. */
export type LiveSyncHandlers = {
  onState: (live: PuppetState['live']) => void;
  onCommit: (commit: LiveCommit) => void;
  onError: (error: Pose3DError) => void;
};

/** The open sync; `close` is idempotent and silences every handler. */
export type LiveSync = { close: () => void };

/**
 * Connects as `actor` from `sinceRevision`: state `connecting`, then `open` after welcome (missed commits are reported
 * in order first), `off` when the socket closes or fails to open. After `close()` nothing is reported.
 */
export function liveSync(api: EditorApi, puppetId: string, actor: string, sinceRevision: number, handlers: LiveSyncHandlers): LiveSync {
  let closed = false;
  let session: { close: () => void } | null = null;
  const live = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A): void => { if (!closed) fn(...args); };
  handlers.onState('connecting');
  api.puppets.live(puppetId, actor, {
    onWelcome: live((m) => { for (const c of m.missed) handlers.onCommit(c); }),
    onCommitted: live((m) => { handlers.onCommit({ revision: m.revision, actor: m.actor, commands: m.commands }); }),
    onError: live((e) => { handlers.onError(e); }),
    onClose: live(() => { handlers.onState('off'); }),
  }, sinceRevision).then((s) => {
    session = s;
    if (closed) s.close();
    else handlers.onState('open');
  }, live((e: unknown) => {
    handlers.onState('off');
    handlers.onError(asPose3DError(e));
  }));
  return {
    close: () => {
      if (closed) return;
      closed = true;
      session?.close();
    },
  };
}
